/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Server-safe SCHEMA-ONLY copies of the web editor's custom TipTap extensions.
 *
 * Why this file exists: `renderToMarkdown` only needs a ProseMirror *schema* so
 * `Node.fromJSON` can parse a document and preserve every node/mark name +
 * attribute. The editor's real extensions (web/src/tiptap/tiptap-extensions/*)
 * carry React node-views, commands, input rules and store/trpc imports that drag
 * the whole browser bundle into the server's module graph (issue #520).
 *
 * Each export below mirrors the editor extension's schema fields verbatim —
 * name, group/content/marks/flags, attribute keys + defaults, and renderHTML —
 * while dropping addNodeView / addCommands / addKeyboardShortcuts /
 * addProseMirrorPlugins (none of which affect JSON→Markdown serialization).
 * Attribute defaults are copied exactly (including nanoid id defaults) so output
 * is byte-identical to the editor's renderToMarkdown. Guarded by the snapshot
 * tests in ../__tests__/render.test.ts.
 *
 * KEEP IN SYNC: if an editor extension gains a serialized attribute, mirror it
 * here. The parity test fails loudly on divergence.
 */
import { Mark, Node, mergeAttributes } from '@tiptap/core';
import { nanoid } from 'nanoid';

// ─── ui-components ───────────────────────────────────────────────────────────

export const CardSchema = Node.create({
  name: 'Card',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      title: { default: 'Card Title' },
      icon: { default: 'sparkles' },
      iconType: { default: 'REGULAR' },
      iconCategory: { default: 'LUCIDE' },
      iconColor: { default: '#3b82f6' },
      showIcon: { default: false },
      href: { default: '' },
      imagePath: { default: '' },
      callToAction: { default: '' },
      id: { default: null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'Card' }), 0];
  },
});

export const CardGroupSchema = Node.create({
  name: 'CardGroup',
  group: 'block',
  content: 'Card+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      cols: { default: 2 },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'CardGroup',
        class: 'group/cardgroup relative my-5 p-0.5',
      }),
      0,
    ];
  },
});

export const AdmonitionSchema = Node.create({
  name: 'Admonition',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: nanoid(10) },
      admonitionType: { default: 'note' },
      title: { default: '' },
      showTitle: { default: true },
      icon: { default: 'sparkles' },
      iconType: { default: 'REGULAR' },
      iconColor: { default: '#3b82f6' },
      showIcon: { default: true },
      useCustomIcon: { default: false },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'Admonition' }), 0];
  },
});

export const InternalNoteSchema = Node.create({
  name: 'InternalNote',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: nanoid(10) },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'InternalNote' }), 0];
  },
});

export const StepsSchema = Node.create({
  name: 'Steps',
  group: 'block',
  content: 'Step+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      flowId: { default: null },
      stepBlockId: { default: null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'section',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Steps', class: 'relative my-5 p-0.5' }),
      0,
    ];
  },
});

export const StepSchema = Node.create({
  name: 'Step',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      number: { default: 1 },
      title: { default: '' },
      guideAction: { default: '' },
      showTitle: { default: false },
      showCaption: { default: false },
      icon: { default: 'sparkles' },
      iconType: { default: 'REGULAR' },
      iconCategory: { default: 'LUCIDE' },
      iconColor: { default: '#3b82f6' },
      showIcon: { default: false },
      cropStyle: { default: 'cropped' },
      screenshotUrl: { default: null },
      pageUrl: { default: null },
      screenshotWidth: { default: null },
      screenshotHeight: { default: null },
      screenshotZoomLevel: { default: 1 },
      screenshotPanX: { default: 0 },
      screenshotPanY: { default: 0 },
      align: { default: 'center' },
      interactionPoints: { default: null },
      elementBox: { default: null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'Step' }), 0];
  },
});

export const ScreenshotSchema = Node.create({
  name: 'Screenshot',
  group: 'block',
  content: 'text*',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      id: { default: null },
      url: { default: '' },
      width: { default: null },
      height: { default: null },
      initialWidth: { default: null },
      initialHeight: { default: null },
      align: { default: 'center' },
      caption: { default: '' },
      zoomLevel: { default: 1 },
      placeholderId: { default: null },
      fromPlaceholderId: { default: null },
      interactionPoints: { default: null },
      step_id: { default: '' },
      alt: { default: '' },
      inline: { default: false },
      cropStyle: { default: 'none' },
      elementBox: { default: null },
      selectedTopicId: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'Screenshot' }), 0];
  },
});

export const TabsSchema = Node.create({
  name: 'Tabs',
  group: 'block',
  content: 'Tab+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Tabs',
        class: 'group/tabs relative my-5 p-0.5',
      }),
      0,
    ];
  },
});

export const TabSchema = Node.create({
  name: 'Tab',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      title: { default: '' },
      icon: { default: 'sparkles' },
      iconType: { default: 'REGULAR' },
      iconCategory: { default: 'LUCIDE' },
      iconColor: { default: '#3b82f6' },
      showIcon: { default: false },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Tab', class: 'tab-element mb-1 p-0' }),
      0,
    ];
  },
});

export const AccordionSchema = Node.create({
  name: 'Accordion',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      title: { default: 'Accordion' },
      description: { default: '' },
      defaultOpen: { default: false },
      icon: { default: 'sparkles' },
      iconCategory: { default: 'LUCIDE' },
      iconType: { default: 'REGULAR' },
      iconColor: { default: '#3b82f6' },
      showIcon: { default: false },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Accordion',
        class:
          'accordion-item group/item border-border relative my-5 overflow-hidden rounded-[12px] border border-solid p-0',
      }),
      0,
    ];
  },
});

export const AccordionGroupSchema = Node.create({
  name: 'AccordionGroup',
  group: 'block',
  content: 'Accordion+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'AccordionGroup',
        class: 'group/accordiongroup relative my-5 p-0.5',
      }),
      0,
    ];
  },
});

export const GlossarySchema = Node.create({
  name: 'Glossary',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: false,
  addAttributes() {
    return {
      term: { default: '' },
      definition: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'strong',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Glossary',
        'data-glossary': 'true',
        class: 'font-bold',
      }),
      0,
    ];
  },
});

export const CitationSchema = Node.create({
  name: 'Citation',
  group: 'inline',
  inline: true,
  content: 'text*',
  draggable: false,
  addAttributes() {
    return {
      id: { default: '' },
      title: { default: '' },
      topicId: { default: '' },
      docId: { default: '' },
      slug: { default: '' },
      sourceType: { default: '' },
      updatedAt: { default: '' },
      isBrokenLink: { default: false },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Citation', class: 'citation-node' }),
      0,
    ];
  },
});

export const FolderContentsSchema = Node.create({
  name: 'FolderContents',
  group: 'block',
  content: '',
  atom: true,
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      layout: { default: 'grid-2' },
      showIcons: { default: true },
      showDescriptions: { default: true },
      selectedItems: { default: [] },
      customOrder: { default: [] },
      topicData: { default: [] },
      selectedTopicId: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'FolderContents',
        class: 'group/folder-contents relative p-0',
      }),
    ];
  },
});

export const MermaidSchema = Node.create({
  name: 'mermaid',
  group: 'block',
  content: 'text*',
  marks: '',
  atom: true,
  isolating: true,
  defining: true,
  addOptions() {
    return { HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      id: { default: null },
      code: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes, node }) {
    const code = node.attrs.code || '';
    return [
      'pre',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-dg-id': node.attrs.id || undefined,
        'data-id': node.attrs.id || undefined,
      }),
      ['code', { class: 'language-mermaid' }, code],
    ];
  },
});

export const ToggleSchema = Node.create({
  name: 'toggle',
  group: 'block',
  content: 'block+',
  isolating: true,
  defining: true,
  addAttributes() {
    return {
      id: { default: null },
      title: { default: 'Toggle' },
      defaultOpen: { default: false },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'toggle', class: 'toggle-item' }),
      0,
    ];
  },
});

export const QuotedSourcesSchema = Node.create({
  name: 'quotedSources',
  group: 'block',
  atom: true,
  draggable: false,
  // quotedSources is NOT overridden by renderToMarkdown's nodeMapping, so its own
  // renderHTML runs — keep the `sources` attribute's renderHTML so HTMLAttributes
  // matches the editor (omit when empty; otherwise serialize to data-sources).
  addAttributes() {
    return {
      sources: {
        default: [],
        renderHTML: (attributes: any) => {
          if (!attributes.sources || attributes.sources.length === 0) {
            return {};
          }
          return { 'data-sources': JSON.stringify(attributes.sources) };
        },
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-quoted-sources': '',
        class: 'quoted-sources-block',
      }),
      0,
    ];
  },
});

// ─── common ──────────────────────────────────────────────────────────────────

export const ActionPillSchema = Node.create({
  name: 'actionPill',
  priority: 1000,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addOptions() {
    return { HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      provider: { default: '' },
      action: { default: '' },
      params: { default: '{}' },
      outputVar: { default: '' },
      label: { default: '' },
      checkId: { default: '' },
      tool: { default: '' },
      connectionId: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const label =
      HTMLAttributes['data-label'] ||
      `${HTMLAttributes['data-provider']}: ${HTMLAttributes['data-action']}`;
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-action-pill': '',
        class: 'action-pill',
      }),
      label,
    ];
  },
});

export const RunbookRefMentionSchema = Node.create({
  name: 'runbookRefMention',
  priority: 1000,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      ref: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-runbook-ref': '' }),
      HTMLAttributes['data-ref'] || '',
    ];
  },
});

enum ColumnLayout {
  SidebarLeft = 'sidebar-left',
  SidebarRight = 'sidebar-right',
  TwoColumn = 'two-column',
}

export const ColumnSchema = Node.create({
  name: 'column',
  content: 'block+',
  isolating: true,
  addAttributes() {
    return {
      position: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'column' }), 0];
  },
});

export const ColumnsSchema = Node.create({
  name: 'columns',
  group: 'columns',
  content: 'column column',
  defining: true,
  isolating: true,
  addAttributes() {
    return {
      layout: { default: ColumnLayout.TwoColumn },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', { 'data-type': 'columns', class: `layout-${HTMLAttributes.layout}` }, 0];
  },
});

export const ImageUploadSchema = Node.create({
  name: 'imageUpload',
  group: 'block',
  draggable: true,
  selectable: true,
  atom: true,
  addAttributes() {
    return {
      accept: { default: 'image/*' },
      limit: { default: 1 },
      maxSize: { default: 0 },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'image-upload' }, HTMLAttributes)];
  },
});

export const VideoSchema = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addOptions() {
    return { HTMLAttributes: {} };
  },
  // video is NOT overridden by renderToMarkdown's nodeMapping, so its own
  // renderHTML runs — keep each attribute's renderHTML so HTMLAttributes matches
  // the editor (omit null src/poster/caption; align serializes to data-align).
  addAttributes() {
    return {
      src: {
        default: null,
        renderHTML: (attributes: any) => (!attributes.src ? {} : { src: attributes.src }),
      },
      poster: {
        default: null,
        renderHTML: (attributes: any) => (!attributes.poster ? {} : { poster: attributes.poster }),
      },
      caption: {
        default: null,
        renderHTML: (attributes: any) =>
          !attributes.caption ? {} : { 'data-caption': attributes.caption },
      },
      width: {
        default: '100%',
        renderHTML: (attributes: any) => ({ width: attributes.width || '100%' }),
      },
      height: {
        default: 'auto',
        renderHTML: (attributes: any) => ({ height: attributes.height || 'auto' }),
      },
      align: {
        default: 'center',
        renderHTML: (attributes: any) => ({ 'data-align': attributes.align || 'center' }),
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-type': 'video' }),
      [
        'video',
        {
          src: HTMLAttributes.src,
          poster: HTMLAttributes.poster,
          controls: true,
          width: HTMLAttributes.width,
          height: HTMLAttributes.height,
          style: `width: ${HTMLAttributes.width}; height: ${HTMLAttributes.height};`,
        },
      ],
    ];
  },
});

export const InlineButtonSchema = Node.create({
  name: 'inlineButton',
  priority: 1000,
  group: 'inline',
  inline: true,
  atom: true,
  addOptions() {
    return { HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      text: { default: 'Button' },
      href: { default: '' },
      variant: { default: 'primary' },
      icon: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'button',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-inline-button': '',
        class: `inline-button inline-button--${HTMLAttributes.variant || 'primary'}`,
      }),
      HTMLAttributes.text || 'Button',
    ];
  },
});

export const InlineImageSchema = Node.create({
  name: 'inlineImage',
  priority: 1000,
  group: 'inline',
  inline: true,
  atom: true,
  addOptions() {
    return { HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      src: { default: '' },
      alt: { default: '' },
      title: { default: '' },
      darkSrc: { default: null },
      display: { default: 'inline' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const { darkSrc, display } = HTMLAttributes;
    if (darkSrc) {
      return [
        'span',
        { 'data-inline-image-wrapper': '', class: 'inline-image-wrapper' },
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            'data-inline-image': '',
            class: `inline-image inline-image--${display || 'inline'} light-image`,
          }),
        ],
        [
          'img',
          mergeAttributes(this.options.HTMLAttributes, {
            src: darkSrc,
            alt: HTMLAttributes.alt || '',
            title: HTMLAttributes.title || '',
            'data-inline-image': '',
            class: `inline-image inline-image--${display || 'inline'} dark-image`,
          }),
        ],
      ];
    }
    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-inline-image': '',
        class: `inline-image inline-image--${display || 'inline'}`,
      }),
    ];
  },
});

// ─── compliance ──────────────────────────────────────────────────────────────

export const TemplatePlaceholderSchema = Node.create({
  name: 'templatePlaceholder',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return {
      name: { default: '' },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'placeholder' }), 0];
  },
});

// ─── marks ───────────────────────────────────────────────────────────────────

export const KbdSchema = Mark.create({
  name: 'kbd',
  excludes: '_',
  addOptions() {
    return { HTMLAttributes: {} };
  },
  renderHTML({ HTMLAttributes }) {
    return ['kbd', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

export const SmallSchema = Mark.create({
  name: 'small',
  addOptions() {
    return { HTMLAttributes: {} };
  },
  renderHTML({ HTMLAttributes }) {
    return ['small', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
});

/** Every custom node/mark schema, in editor registration order where it matters. */
export const CustomSchemaExtensions = [
  // ui-components
  CardGroupSchema,
  CardSchema,
  AdmonitionSchema,
  InternalNoteSchema,
  StepsSchema,
  StepSchema,
  ScreenshotSchema,
  TabsSchema,
  TabSchema,
  AccordionSchema,
  AccordionGroupSchema,
  GlossarySchema,
  CitationSchema,
  FolderContentsSchema,
  MermaidSchema,
  ToggleSchema,
  QuotedSourcesSchema,
  // common
  ColumnsSchema,
  ColumnSchema,
  ImageUploadSchema,
  InlineButtonSchema,
  InlineImageSchema,
  ActionPillSchema,
  RunbookRefMentionSchema,
  KbdSchema,
  SmallSchema,
  VideoSchema,
  // compliance
  TemplatePlaceholderSchema,
] as any[];
